import type { ResetPasswordState } from '@/pages/ResetPassword/reset-password';

import {
  mapAuthResponse,
  mapChangePasswordRequest,
  mapResetPasswordRequest,
} from '@/mappers/auth.mapper';

import type { ResetPasswordFormValues } from '@/schemas/auth.schema';
import type { ChangePasswordFormValues } from '@/schemas/user.schema';

import apiClient from '@/lib/api-client';
import { raiseAxiosErrorOrDefault } from '@/lib/error';

import type {
  AuthResponse,
  ForgotPasswordConfirmRequest,
  LoginCredentials,
  ResetPasswordChallenge,
} from '@/types/auth.type';

export const loginUser = async (
  credentials: LoginCredentials
): Promise<AuthResponse | ResetPasswordChallenge> => {
  try {
    const response = await apiClient.post<AuthResponse | ResetPasswordChallenge>(
      '/auth/login',
      credentials,
      mapAuthResponse
    );
    return response;
  } catch (error) {
    raiseAxiosErrorOrDefault(error, 'Invalid login credentials');
  }
};

export const changePassword = async (data: ChangePasswordFormValues): Promise<void> => {
  try {
    await apiClient.patch<void>('/auth/change-password', mapChangePasswordRequest(data));
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to change password');
  }
};

export const resetPassword = async (
  data: ResetPasswordFormValues,
  extra: ResetPasswordState
): Promise<void> => {
  try {
    await apiClient.put<AuthResponse>('/auth/reset-password', mapResetPasswordRequest(data, extra));
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to reset password');
  }
};

export const forgotPassword = async (username: string): Promise<void> => {
  try {
    await apiClient.post<void>('/auth/forgot-password', { username });
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to send password reset code');
  }
};

export const forgotPasswordConfirm = async (data: ForgotPasswordConfirmRequest): Promise<void> => {
  try {
    await apiClient.post<void>('/auth/forgot-password/confirm', data);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to confirm password reset');
  }
};
